"use client"

import { useState } from "react"
import { Link } from "@tanstack/react-router"
import { Briefcase, Home, MoreHorizontal, Search, Users } from "lucide-react"

import { RobinAvatar } from "@/components/agentic-ats/robin/robin-avatar"
import { ThemeToggle } from "@/components/theme-toggle"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet"
import { useAuth } from "@/hooks/useAuth"
import { ALL_NAV } from "./nav-routes"
import { cn } from "@/lib/utils"

/**
 * Mobile bottom nav, from canvas artboard 12b.
 *
 * Four routes plus Robin. Robin is a peer here rather than a pane because below
 * this width there is no room for a split — the phone cannot show the ATS and
 * the thread at once, so the conversation becomes a destination.
 *
 * Note this is the *standalone phone* nav. When the ATS panel is merely dragged
 * narrow on a desktop, the chat pane is already on screen beside it, so the
 * Robin tab would be a duplicate; the shell renders this only below the real
 * mobile breakpoint.
 *
 * The sixth tab is not in 12b, and is here anyway. No app bar renders below this
 * width — the header is `hidden md:flex` — so this nav is the entire navigation
 * of the product, and the four tabs the artboard draws left Placements,
 * Community, Tasks, Approvals, Calendar, Settings and even Log out with no way
 * to reach them short of typing a URL. The artboard not drawing an overflow is
 * the artboard being incomplete, not a decision to strand seven destinations.
 */
const TABS = [
  { name: "Home", href: "/dashboard", icon: Home },
  { name: "Jobs", href: "/jobs", icon: Briefcase },
  { name: "Candidates", href: "/candidates", icon: Users },
  { name: "Searches", href: "/searches", icon: Search },
] as const

/**
 * Whatever the tabs could not fit, derived rather than listed again — a route
 * added to `nav-routes.ts` turns up here on its own instead of quietly
 * disappearing on phones.
 */
const MORE = ALL_NAV.filter((route) => !TABS.some((tab) => tab.href === route.href))

const itemClass =
  "flex flex-1 flex-col items-center justify-center gap-[5px] text-[10px] text-muted-foreground transition-colors"

export function MobileNav({
  onOpenRobin,
  robinNeedsAttention = false,
}: {
  onOpenRobin: () => void
  robinNeedsAttention?: boolean
}) {
  const [moreOpen, setMoreOpen] = useState(false)
  const { user, logout } = useAuth()
  const userInitial = user?.name?.[0]?.toUpperCase() ?? "?"

  return (
    <>
      <nav
        aria-label="Main"
        className="relative z-[5] flex h-[62px] shrink-0 border-t bg-chrome md:hidden"
      >
        {TABS.map((item) => (
          <Link
            key={item.href}
            to={item.href}
            className={itemClass}
            activeProps={{ className: cn(itemClass, "text-brand-ink") }}
          >
            <item.icon className="h-5 w-5" />
            <span>{item.name}</span>
          </Link>
        ))}

        <button
          type="button"
          onClick={() => setMoreOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={moreOpen}
          className={cn(itemClass, moreOpen && "text-brand-ink")}
        >
          <MoreHorizontal className="h-5 w-5" />
          <span>More</span>
        </button>

        <button type="button" onClick={onOpenRobin} className={itemClass}>
          <span className="relative flex">
            <RobinAvatar state="idle" className="h-[22px] w-[22px]" />
            {robinNeedsAttention && (
              <span className="absolute -right-[3px] -top-[2px] h-2 w-2 rounded-full bg-attention ring-2 ring-chrome" />
            )}
          </span>
          <span>Robin</span>
        </button>
      </nav>

      {/*
        Everything the desktop keeps in its overflow menu and its avatar menu,
        in one sheet — because the phone has neither of those to fall back on.
      */}
      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="bottom" className="rounded-t-[14px] px-0 pb-[env(safe-area-inset-bottom)]">
          <SheetTitle className="sr-only">More</SheetTitle>

          <div className="flex items-center gap-3 border-b px-5 pb-3.5">
            <Avatar className="h-9 w-9">
              <AvatarImage src={user?.picture ?? undefined} alt="" />
              <AvatarFallback className="bg-primary/15 text-[12px] font-medium text-brand-ink">
                {userInitial}
              </AvatarFallback>
            </Avatar>
            <span className="min-w-0">
              <span className="block truncate text-[13px] font-medium leading-tight">
                {user?.name ?? ""}
              </span>
              <span className="mt-[3px] block truncate text-[11.5px] leading-tight text-muted-foreground">
                {user?.email ?? ""}
              </span>
            </span>
          </div>

          <div className="flex flex-col py-1.5">
            {MORE.map((route) => (
              <Link
                key={route.href}
                to={route.href}
                onClick={() => setMoreOpen(false)}
                className="flex h-11 items-center px-5 text-[13.5px] transition-colors hover:bg-foreground/[0.04]"
                activeProps={{
                  className:
                    "flex h-11 items-center px-5 text-[13.5px] font-medium text-brand-ink",
                }}
              >
                {route.name}
              </Link>
            ))}

            <Link
              to="/settings"
              onClick={() => setMoreOpen(false)}
              className="flex h-11 items-center border-t px-5 text-[13.5px] transition-colors hover:bg-foreground/[0.04]"
            >
              Settings
            </Link>

            <div className="flex h-11 items-center justify-between border-t px-5 text-[13.5px]">
              <span>Theme</span>
              <ThemeToggle />
            </div>

            <button
              type="button"
              onClick={() => {
                setMoreOpen(false)
                logout()
              }}
              className="flex h-11 items-center border-t px-5 text-left text-[13.5px] text-destructive transition-colors hover:bg-destructive/[0.06]"
            >
              Log out
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  )
}
